import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import styled from 'styled-components';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCoffee, 
  faUtensils, 
  faClock, 
  faLeaf, 
  faWineGlassAlt,
  faCalendarAlt,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import SEO from '../../common/SEO';

const perfectSettingImage = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/Photo%2027-05-2025%2C%2014%2016%2002.webp';

// Import placeholder images - replace with actual images

const castleImage = 'https://BelvoirCastle.b-cdn.net/Castle/castle.webp';

const tearoomBanner = 'https://BelvoirCastle.b-cdn.net/Banners/tearoomBan.webp';

const teaRoomInterior = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/tearoom-interior.webp';
const lunchImage = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/tea-food/lunch.webp';
const afternoonTeaImage = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/afternoon-tea.webp';
const cakesImage = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/tea-food/cakes.webp';

const GET_TEAROOM_DATA = gql`
  query GetTeaRoomData {
    siteSettings(where: {title: "Global Site Settings"}) {
      nodes {
        siteSettingsFields {
          contactInfo {
            phone
            email
          }
        }
      }
    }
  }
`;

const AviaryTearoomContainer = styled.div`
  min-height: 100vh;
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const FeatureSection = styled.div`
  display: flex;
  margin: 5rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 992px) {
    flex-direction: ${props => props.imagePosition === 'right' ? 'column-reverse' : 'column'};
  }
`;

const FeatureImage = styled.div`
  flex: 1;
  min-height: 500px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  
  @media (max-width: 992px) {
    min-height: 300px;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
  padding: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const FeatureButton = styled(Link)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  border: 2px solid transparent;
  border-radius: 25px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const MenuSection = styled.div`
  margin: 5rem 0;
`;

const MenuTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const MenuTab = styled.button`
  background: ${props => props.active ? props.theme.colors.accent : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.accent};
  border: 2px solid ${props => props.active ? props.theme.colors.accent : props.theme.colors.accent};
  padding: 0.8rem 1.5rem;
  margin: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 25px;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.accent : 'transparent'};
    color: ${props => props.active ? 'white' : props.theme.colors.accent};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuGroupTitle = styled.h3`
  grid-column: 1 / -1;
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin: 1rem 0 0;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const MenuItem = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  ${props => props.$wide && 'grid-column: 1 / -1;'}
  ${props => props.$center && `
    text-align: center;

    h4 {
      justify-content: center;
      gap: 1.25rem;
    }

    ul {
      justify-content: center;
    }
  `}
`;

const MenuItemTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  
  span {
    color: ${props => props.theme.colors.accent};
  }
`;

const MenuItemDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
`;

const TeaTypes = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.5rem;
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  line-height: 1.6;
`;

const MenuItemPrice = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.accent};
`;

const MenuItemTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const MenuItemTag = styled.span`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
`;

const MenuKey = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem 1.5rem;
  list-style: none;
  margin: 0 0 2.5rem;
  padding: 0;
`;

const MenuKeyItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: ${props => props.theme.colors.primary};
`;

const HighlightSection = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
  margin: 5rem 0;
`;

const HighlightContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HighlightTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.2rem;
  color: white;
  margin-bottom: 1.5rem;
`;

const HighlightText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const HighlightButton = styled(Link)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  border: 2px solid transparent;
  border-radius: 25px;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const HighlightActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  
  svg {
    color: white;
    font-size: 32px;
  }
`;

const FeatureCardTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const FeatureCardDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const GallerySection = styled.div`
  margin: 5rem 0;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImage = styled.div`
  height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const InfoSection = styled.div`
  margin: 5rem 0;
  text-align: center;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const InfoTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const TeaRoomLink = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 2rem;
  border: 2px solid transparent;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin: 4rem 0;
  text-align: center;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const AviaryTearoom = () => {
  const { loading, error, data } = useQuery(GET_TEAROOM_DATA);
  const [activeTab, setActiveTab] = React.useState('breakfast');
  
  // Extract contact info with fallback values
  const contactInfo = data?.siteSettings?.nodes?.[0]?.siteSettingsFields?.contactInfo || {
    phone: "01476 871001",
    email: "tearoom@belvoircastle.com"
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const renderMenuItems = () => {
    switch(activeTab) {
      case 'breakfast':
        return (
          <MenuGrid>
            <MenuItem>
              <MenuItemTitle>
                BACON BREAKFAST ROLL <span>£5.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served warm in soft sourdough bun
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                SAUSAGE BREAKFAST ROLL <span>£5.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served warm in soft sourdough bun
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                SCRAMBLED EGGS ON SOURDOUGH <span>£8.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Add salmon £4.00
              </MenuItemDescription>
              <MenuItemDescription>
                Add bacon £3.00
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>V</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>
          </MenuGrid>
        );
      
      case 'lunch':
        return (
          <MenuGrid>
            <MenuItem>
              <MenuItemTitle>
                TOASTED TEACAKE <span>£5.25</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served with jam and butter
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>V</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                CREAM TEA <span>£10.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served with jam and cream
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Vg</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                SOUP OF THE DAY <span>£9.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served with a mini bread loaf and butter
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Vg</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                SEASONAL QUICHE OF THE DAY <span>£12.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served with lightly salted crisps and a salad garnish
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                CAESAR SALAD <span>£9.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Cos lettuce, parmesan and croutons served with a Caesar style salad dressing
              </MenuItemDescription>
              <MenuItemDescription>
                Add a roasted chicken breast £4.00
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Vg</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                CAPRESE SALAD <span>£10.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Fresh plum tomato, buffalo mozzarella served with pesto, fresh rocket and balsamic glaze
              </MenuItemDescription>
              <MenuItemDescription>
                Add toasted focaccia £2.00
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>V</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>
          </MenuGrid>
        );
      
      case 'sandwiches':
        return (
          <MenuGrid>
            <MenuItem>
              <MenuItemTitle>
                CHICKEN SCHNITZEL BAGUETTE <span>£13.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served with sundried tomato & rocket
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                ROAST MED VEG AND HUMMUS <span>£12.95</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served in seeded sourdough
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Vg</MenuItemTag>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                SMOKED SALMON & DILL CREAM CHEESE <span>£14.95</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Served in seeded sourdough
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                ROAST BEEF BAGUETTE <span>£13.95</span>
              </MenuItemTitle>
              <MenuItemDescription>
                With smoked Applewood cheddar and red onion marmalade
              </MenuItemDescription>
              <MenuItemTags>
                <MenuItemTag>Gf*</MenuItemTag>
              </MenuItemTags>
            </MenuItem>
          </MenuGrid>
        );

      case 'tipples':
        return (
          <MenuGrid>
            <MenuItem>
              <MenuItemTitle>
                BELVOIR GIN & TONIC <span>£7.95</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Original Pure Gin | Raspberry & Elderflower
              </MenuItemDescription>
              <MenuItemDescription>
                Serves with Fevertree tonic, light tonic or light cucumber
              </MenuItemDescription>
              <MenuItemDescription>
                40% ABV
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>
                PERONI <span>£4.50</span>
              </MenuItemTitle>
              <MenuItemDescription>
                5% ABV
              </MenuItemDescription>
            </MenuItem>

            <MenuGroupTitle>Belvoir Wines</MenuGroupTitle>

            <MenuItem>
              <MenuItemTitle>BELVOIR ENGLISH SPARKLING ROSÉ</MenuItemTitle>
              <MenuItemPrice>125ml £11.95, Bottle £45.00</MenuItemPrice>
              <MenuItemDescription>
                Green apple aromas and subtle floral notes of acacia. Refreshing palate, light, crisp finish.
              </MenuItemDescription>
              <MenuItemDescription>
                11.5% ABV
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>BELVOIR ENGLISH SPARKLING</MenuItemTitle>
              <MenuItemPrice>125ml £11.95, Bottle £45.00</MenuItemPrice>
              <MenuItemDescription>
                Delicate, dry rosé with notes of white peach and red berries.
              </MenuItemDescription>
              <MenuItemDescription>
                11.5% ABV
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>BELVOIR WHITE</MenuItemTitle>
              <MenuItemPrice>175ml £8.95, Bottle £33.00</MenuItemPrice>
              <MenuItemDescription>
                Honeydew melon enlivened by citrus, with honey. Lemon and apricot on the palate.
              </MenuItemDescription>
              <MenuItemDescription>
                11.5% ABV
              </MenuItemDescription>
            </MenuItem>

            <MenuItem>
              <MenuItemTitle>BELVOIR ROSÉ</MenuItemTitle>
              <MenuItemPrice>175ml £9.25, Bottle £33.00</MenuItemPrice>
              <MenuItemDescription>
                Subtle notes of pink grapefruit, strawberries and raspberries.
              </MenuItemDescription>
              <MenuItemDescription>
                11.5% ABV
              </MenuItemDescription>
            </MenuItem>
          </MenuGrid>
        );
      
      case 'desserts':
        return (
          <MenuGrid>
            <MenuGroupTitle>Hot Drinks</MenuGroupTitle>
            <MenuItem>
              <MenuItemTitle>
                SINGLE ESPRESSO <span>£3.25</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                DOUBLE ESPRESSO <span>£3.95</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                AMERICANO <span>£3.95</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                FLAT WHITE <span>£4.15</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                LATTE <span>£4.25</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                CAPPUCCINO <span>£4.25</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                MOCHA <span>£4.35</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                LUXURY HOT CHOCOLATE <span>£4.50</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                CHAI LATTE <span>£4.50</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                DIRTY CHAI LATTE <span>£4.95</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuGroupTitle>Tea</MenuGroupTitle>
            <MenuItem $wide $center>
              <MenuItemTitle>
                TREGOTHNAN TEA <span>£3.95</span>
              </MenuItemTitle>
              <MenuItemDescription>
                Tregothnan Estate in Cornwall is the primary and most famous grower of tea in England, pioneering the first English grown tea in 1999.
              </MenuItemDescription>
              <TeaTypes>
                <li>Breakfast</li>
                <li>Lemon Verbena</li>
                <li>Earl Grey</li>
                <li>Red Berry</li>
                <li>Peppermint</li>
                <li>Decaf Breakfast</li>
                <li>Green</li>
              </TeaTypes>
            </MenuItem>
            <MenuGroupTitle>Soft Drinks</MenuGroupTitle>
            <MenuItem>
              <MenuItemTitle>
                BELVOIR FARM SPARKLING <span>£4.25</span>
              </MenuItemTitle>
              <TeaTypes>
                <li>Elderflower</li>
                <li>Lemonade</li>
                <li>Raspberry Lemonade</li>
                <li>Ginger Beer</li>
                <li>Pink Apple</li>
              </TeaTypes>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                FROBISHER'S STILL FRUIT JUICE <span>£4.50</span>
              </MenuItemTitle>
              <TeaTypes>
                <li>Apple</li>
                <li>Orange</li>
              </TeaTypes>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                RADNOR STILL OR SPARKLING 330ml <span>£2.50</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                COKE, DIET COKE OR FANTA 330ml <span>£2.95</span>
              </MenuItemTitle>
            </MenuItem>
            <MenuItem>
              <MenuItemTitle>
                CHILDREN'S JUICE CARTONS 125ml <span>£2.00</span>
              </MenuItemTitle>
              <TeaTypes>
                <li>Apple</li>
                <li>Orange</li>
              </TeaTypes>
            </MenuItem>
          </MenuGrid>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <AviaryTearoomContainer>
      <SEO 
        title="Aviary Tearoom at Belvoir Castle - Traditional Tea Room"
        description="Experience the charm of our Aviary Tearoom at Belvoir Castle. Enjoy light lunches, homemade cakes, and a selection of fine teas in a beautiful historic setting."
        keywords="Aviary Tearoom, Belvoir Castle, tea room, light lunches, homemade cakes, afternoon tea, Leicestershire"
        canonicalUrl="https://www.belvoircastle.com/castle-dining/aviary-tearoom"
        ogImage={tearoomBanner}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Restaurant",
          "name": "Aviary Tearoom at Belvoir Castle",
          "description": "Experience the charm of our Aviary Tearoom at Belvoir Castle. Enjoy light lunches, homemade cakes, and a selection of fine teas in a beautiful historic setting.",
          "image": tearoomBanner,
          "servesCuisine": "British",
          "priceRange": "££",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Belvoir Castle",
            "addressLocality": "Grantham",
            "addressRegion": "Leicestershire",
            "postalCode": "NG32 1PE",
            "addressCountry": "UK"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "10:00",
            "closes": "17:00"
          },
          "menu": "https://www.belvoircastle.com/castle-dining/aviary-tearoom/menu",
          "acceptsReservations": true,
          "telephone": "+441476871001"
        }}
      />
      <ParallaxBanner 
        title="The Aviary Tearoom"
        subtitle="A Culinary Haven at Belvoir Castle"
        image={tearoomBanner}
      />
      
      <ContentContainer>
        <SectionTitle>Welcome to The Aviary Tearoom</SectionTitle>
        <IntroText>
          Nestled in the stunning surroundings of Belvoir Castle, the newly refurbished Aviary Tearoom 
          is the perfect place to relax and enjoy a delicious treat. With its charming decor and comfortable seating, 
          the tearoom offers a tranquil escape from the hustle and bustle of daily life.
        </IntroText>
        
        <FeatureSection imagePosition="left">
          <FeatureImage image={afternoonTeaImage} />
          <FeatureContent>
            <FeatureTitle>A Heritage-Inspired Dining Experience</FeatureTitle>
            <FeatureDescription>
              The Aviary Tearoom draws inspiration from Belvoir Castle's rich history and heritage. 
              Our menu celebrates local produce and traditional recipes with a contemporary twist, 
              offering visitors a taste of Belvoir's culinary legacy in elegant surroundings.
            </FeatureDescription>
            <FeatureDescription>
              From hearty breakfasts to light lunches and indulgent treats, every dish is prepared with care 
              using the finest ingredients, many sourced from the Belvoir estate and surrounding countryside.
            </FeatureDescription>
          </FeatureContent>
        </FeatureSection>
        
        <FeaturesGrid>
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faCoffee} />
            </IconWrapper>
            <FeatureCardTitle>Artisan Coffee & Teas</FeatureCardTitle>
            <FeatureCardDescription>
              Enjoy our selection of premium coffees and specialty teas, carefully selected to complement our menu 
              and provide the perfect accompaniment to your visit.
            </FeatureCardDescription>
          </FeatureCard>
          
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faLeaf} />
            </IconWrapper>
            <FeatureCardTitle>Locally Sourced Ingredients</FeatureCardTitle>
            <FeatureCardDescription>
              We take pride in using fresh, seasonal ingredients from local suppliers and the Belvoir estate, 
              ensuring quality and supporting our community.
            </FeatureCardDescription>
          </FeatureCard>
          
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faClock} />
            </IconWrapper>
            <FeatureCardTitle>All-Day Dining</FeatureCardTitle>
            <FeatureCardDescription>
              From breakfast to afternoon tea, we offer a range of dining options throughout the day, 
              perfect for any time of your visit to Belvoir Castle.
            </FeatureCardDescription>
          </FeatureCard>
        </FeaturesGrid>
        
        <HighlightSection>
          <HighlightContent>
            <HighlightTitle>Experience Our Signature Afternoon Tea</HighlightTitle>
            <HighlightText>
              Indulge in our exquisite Afternoon Tea, a celebration of Belvoir's heritage featuring 
              estate-sourced ingredients and artisanal delicacies. Each item tells a story of our rich history 
              and connection to the land, creating a truly memorable experience.
            </HighlightText>
            <HighlightActions>
              <HighlightButton to="/castle-dining/afternoon-tea-booking">Book Afternoon Tea</HighlightButton>
              <HighlightButton to="/castle-dining/afternoon-tea#afternoon-tea-menu">View Afternoon Tea Menu</HighlightButton>
            </HighlightActions>
          </HighlightContent>
        </HighlightSection>
        
        <MenuSection>
          <SectionTitle>Our Menu</SectionTitle>
          <IntroText>
            Explore our seasonal menu featuring fresh, locally-sourced ingredients and heritage-inspired recipes. 
            From hearty breakfasts to light lunches and indulgent treats, there's something for everyone to enjoy.
          </IntroText>

          <MenuKey aria-label="Menu key">
            <MenuKeyItem><MenuItemTag>V</MenuItemTag> Vegetarian</MenuKeyItem>
            <MenuKeyItem><MenuItemTag>Vg</MenuItemTag> Vegan</MenuKeyItem>
            <MenuKeyItem><MenuItemTag>Gf*</MenuItemTag> Gluten free available on request</MenuKeyItem>
          </MenuKey>
          
          <MenuTabs>
            <MenuTab 
              active={activeTab === 'breakfast'} 
              onClick={() => handleTabChange('breakfast')}
            >
              Breakfast
            </MenuTab>
            <MenuTab 
              active={activeTab === 'lunch'} 
              onClick={() => handleTabChange('lunch')}
            >
              Light Bites
            </MenuTab>
            <MenuTab 
              active={activeTab === 'sandwiches'} 
              onClick={() => handleTabChange('sandwiches')}
            >
              Sandwiches
            </MenuTab>
            <MenuTab 
              active={activeTab === 'tipples'} 
              onClick={() => handleTabChange('tipples')}
            >
              Tipples
            </MenuTab>
            <MenuTab 
              active={activeTab === 'desserts'} 
              onClick={() => handleTabChange('desserts')}
            >
              Drinks
            </MenuTab>
          </MenuTabs>
          
          {renderMenuItems()}
        </MenuSection>
        
        <FeatureSection imagePosition="right">
          <FeatureContent>
            <FeatureTitle>The Perfect Setting for Any Occasion</FeatureTitle>
            <FeatureDescription>
              Whether you're enjoying a quiet moment alone, catching up with friends, or celebrating a special occasion, 
              the Aviary Tearoom provides the perfect backdrop. Our elegant interior offers 
              stunning views of the castle grounds, enhancing your dining experience.
            </FeatureDescription>
            <FeatureDescription>
              For those special celebrations, consider our signature Afternoon Tea experience, 
              a truly memorable way to mark any occasion.
            </FeatureDescription>
            <FeatureButton to="/castle-dining/afternoon-tea-booking">Book Afternoon Tea</FeatureButton>
          </FeatureContent>
          <FeatureImage image={perfectSettingImage} />
        </FeatureSection>
        
        <GallerySection>
          <SectionTitle>A Glimpse of The Aviary Tearoom</SectionTitle>
          <GalleryGrid>
            <GalleryImage image={perfectSettingImage} />
            <GalleryImage image={teaRoomInterior} />
            <GalleryImage image={lunchImage} />
            <GalleryImage image={afternoonTeaImage} />
            <GalleryImage image={cakesImage} />
            <GalleryImage image={teaRoomInterior} />
          </GalleryGrid>
        </GallerySection>
        
        <InfoSection>
          <SectionTitle>Visiting Information</SectionTitle>
          <InfoGrid>
            <InfoCard>
              <InfoTitle>Opening Hours</InfoTitle>
              <InfoText>
                <strong>Monday to Sunday:</strong> 10:00am - 5:00pm
              </InfoText>
              <InfoText>
                <strong>Breakfast:</strong> 10:00am - 11:30am
              </InfoText>
              <InfoText>
                <strong>Lunch:</strong> 12:00pm - 4:00pm
              </InfoText>
              <InfoText>
                <strong>Afternoon Tea:</strong> 12:00pm - 5:00pm (pre-booking recommended)
              </InfoText>
            </InfoCard>
            
            <InfoCard>
              <InfoTitle>Reservations</InfoTitle>
              <InfoText>
                While walk-ins are welcome for general dining, we recommend booking in advance for Afternoon Tea 
                to ensure availability, especially during busy periods.
              </InfoText>
              <InfoText>
                <strong>Phone:</strong> <a href={`tel:${contactInfo.phone}`} style={{ color: theme.colors.accent }}>{contactInfo.phone}</a>
              </InfoText>
              <InfoText>
                <strong>Email:</strong> <a href={`mailto:${contactInfo.email}`} style={{ color: theme.colors.accent }}>{contactInfo.email}</a>
              </InfoText>
            </InfoCard>
          </InfoGrid>
        </InfoSection>
        
        <div style={{ textAlign: 'center', margin: '4rem 0' }}>
          <TeaRoomLink to="/castle-dining/afternoon-tea-booking">
            Book Afternoon Tea
          </TeaRoomLink>
        </div>
      </ContentContainer>
      
      <Footer />
    </AviaryTearoomContainer>
  );
};

export default AviaryTearoom;